import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Hand, MessageSquareWarning, Banknote, DoorOpen, Siren, Timer } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* Robbery CEF — /cef/rob/:serverId?store=gas:docks&armed=1
   Transparent overlay (the 3D store stays visible). Phases:
   APPROACH → DIALOGUE (bottom-third RPG box: intimidate / calm / bribe against the
   cashier's rolled mood) → GRAB (register pops, frantic tap window, server-clocked,
   sometimes the till is already dry) → ESCAPE (take, heat, sirens if alarmed). */

const DEMO_STORE = { id: "gas:docks", name: "24/7 Fuel Dock", type: "gas" };

export default function RobCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const storeId = params.get("store") || DEMO_STORE.id;
  const armed = params.get("armed") === "1";

  const [phase, setPhase] = useState("approach"); // approach | dialogue | grab | escape
  const [store, setStore] = useState(DEMO_STORE);
  const [session, setSession] = useState(null);
  const [cashier, setCashier] = useState(null);
  const [options, setOptions] = useState([]);
  const [grabbed, setGrabbed] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [alarm, setAlarm] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bills, setBills] = useState([]); // flying bill particles
  const timerRef = useRef(null);

  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);
  useEffect(() => () => clearInterval(timerRef.current), []);

  const startRobbery = async () => {
    if (demo) {
      setCashier({ mood_hint: "shaking hands", line: "P-please, just take it…" });
      setOptions([
        { id: "intimidate", label: "“Open the register. NOW.”", hint: "Loud. Fast. Risky if they're brave." },
        { id: "calm", label: "“Easy. Nobody gets hurt — just the money.”", hint: "Slower, but calmer hands shake less." },
        { id: "bribe", label: "“A hundred says you never saw me.”", hint: "Rare — but a bought cashier never touches the alarm." },
      ]);
      setPhase("dialogue");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/rob/start`, { store_id: storeId, armed }, { params: { token } });
      setSession(r.data.session);
      setStore(r.data.store);
      setCashier(r.data.cashier);
      setOptions(r.data.options);
      setPhase("dialogue");
    } catch (e) { toast.error(e.response?.data?.detail || "Can't start this robbery"); }
    finally { setBusy(false); }
  };

  const chooseDialogue = async (choice) => {
    if (demo) {
      const ok = Math.random() < 0.7;
      setAlarm(!ok && Math.random() < 0.4);
      setCashier((c) => ({ ...c, line: ok ? "Their hands fly up. The register drawer starts to open." : "They hesitate — you just lost precious seconds." }));
      setPhase("grab"); setSecondsLeft(8);
      timerRef.current = setInterval(() => setSecondsLeft((s) => Math.max(0, +(s - 0.1).toFixed(1))), 100);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/rob/dialogue`, { session, choice }, { params: { token } });
      setCashier((c) => ({ ...c, line: r.data.cashier_line }));
      setAlarm(r.data.alarm);
      setSecondsLeft(r.data.grab_seconds);
      setPhase("grab");
      timerRef.current = setInterval(() => setSecondsLeft((s) => Math.max(0, +(s - 0.1).toFixed(1))), 100);
    } catch (e) { toast.error(e.response?.data?.detail || "The moment slipped"); }
    finally { setBusy(false); }
  };

  const tapGrab = async () => {
    // flying bill particle
    const id = Math.random().toString(36).slice(2);
    setBills((b) => [...b, { id, x: 35 + Math.random() * 30, r: Math.random() * 40 - 20 }]);
    setTimeout(() => setBills((b) => b.filter((x) => x.id !== id)), 900);
    if (demo) { setGrabbed((g) => g + Math.floor(5 + Math.random() * 35)); return; }
    try {
      const r = await api.post(`/nc/servers/${serverId}/rob/grab/${session}/tap`, {}, { params: { token } });
      setGrabbed(r.data.grabbed);
      if (r.data.seconds_left != null) setSecondsLeft(r.data.seconds_left);
      if (r.data.over) { clearInterval(timerRef.current); if (r.data.empty) toast.warning("The drawer is bone dry"); }
    } catch { /* tap dropped — keep grabbing */ }
  };

  const escape = useCallback(async () => {
    clearInterval(timerRef.current);
    if (demo) { setResult({ grabbed, alarm, message: `You walk out $${grabbed} heavier. (preview)` }); setPhase("escape"); return; }
    try {
      const r = await api.post(`/nc/servers/${serverId}/rob/escape`, { session }, { params: { token } });
      setResult(r.data);
    } catch { setResult({ grabbed, alarm, message: "You melt into the street." }); }
    setPhase("escape");
  }, [demo, grabbed, alarm, serverId, session, token]);

  // window slamming shut on the client too
  useEffect(() => {
    if (phase === "grab" && secondsLeft <= 0 && timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, [phase, secondsLeft]);

  return (
    <div data-testid="rob-page" className="min-h-screen bg-transparent relative overflow-hidden">
      <style>{`
        @keyframes ncFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        @keyframes billFly { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(-140px) rotate(40deg); opacity: 0; } }
        @keyframes registerPop { 0% { transform: translateY(18px); } 60% { transform: translateY(-4px); } 100% { transform: translateY(0); } }
        @keyframes alarmPulse { 0%,100% { background-color: rgba(127,29,29,0.25); } 50% { background-color: rgba(239,68,68,0.4); } }
      `}</style>
      {demo && <PreviewRibbon what="robbery" />}

      {/* alarm banner */}
      {alarm && phase !== "approach" && (
        <div data-testid="rob-alarm" className="absolute top-6 inset-x-0 flex justify-center z-20 pointer-events-none">
          <div className="flex items-center gap-2 px-5 py-2.5 border border-red-500/60 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-red-300" style={{ animation: "alarmPulse 1s infinite", backgroundColor: "rgba(20,0,0,0.85)" }}>
            <Siren className="w-4 h-4" /> Silent alarm tripped — dispatch notified
          </div>
        </div>
      )}

      {/* APPROACH — walk in, hand on the grip */}
      {phase === "approach" && (
        <div className="absolute inset-x-0 bottom-10 flex justify-center pointer-events-none">
          <div className="pointer-events-auto w-full max-w-xl border border-white/15 bg-black/85 backdrop-blur-md p-6" style={{ animation: "ncFadeUp 0.3s ease both" }}>
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">{store.name} · {armed ? "your hand rests on the grip" : "empty hands — they'll read that"}</p>
            <p className="font-serif italic text-zinc-200 text-lg leading-snug mt-3">The bell over the door rings. The cashier looks up. This is the moment.</p>
            <div className="flex gap-3 mt-5">
              <button data-testid="rob-begin" disabled={busy} onClick={startRobbery}
                className="flex-1 py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.25em] bg-red-600 hover:bg-red-500 text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-40">
                <Hand className="w-4 h-4" /> {busy ? "…" : "Make your move"}
              </button>
              <button data-testid="rob-walkout" onClick={() => setResult({ grabbed: 0, alarm: false, message: "You buy a soda and leave. Nobody knows what almost happened." }) || setPhase("escape")}
                className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-white/15 text-zinc-400 hover:text-zinc-100 transition-colors">Walk out</button>
            </div>
          </div>
        </div>
      )}

      {/* DIALOGUE — bottom-third RPG box; the store stays fully visible above it */}
      {phase === "dialogue" && (
        <div className="absolute inset-x-0 bottom-8 flex justify-center pointer-events-none">
          <div data-testid="rob-dialogue" className="pointer-events-auto w-full max-w-2xl border border-white/15 bg-black/85 backdrop-blur-md" style={{ animation: "ncFadeUp 0.3s ease both" }}>
            <div className="flex items-start gap-4 p-5 border-b border-white/10">
              <span className="w-12 h-14 shrink-0 border border-white/15 bg-gradient-to-b from-[#1a1d24] to-[#0c0e12] flex items-center justify-center font-mono font-bold text-lg text-zinc-400">?</span>
              <div className="min-w-0">
                <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-500">Cashier · {cashier?.mood_hint}</p>
                <p className="font-serif italic text-[15px] text-zinc-100 leading-snug mt-1.5">"{cashier?.line}"</p>
              </div>
            </div>
            <div className="p-3 flex flex-col gap-1.5">
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-600 px-2 pb-1 flex items-center gap-1.5"><MessageSquareWarning className="w-3 h-3" /> What do you say?</p>
              {options.map((o) => (
                <button key={o.id} data-testid={`rob-say-${o.id}`} disabled={busy} onClick={() => chooseDialogue(o.id)}
                  className="text-left px-4 py-3 border border-white/10 hover:border-red-500/50 hover:bg-red-950/20 transition-colors group disabled:opacity-40">
                  <span className="block font-mono text-[11px] text-zinc-100 group-hover:text-red-200">{o.label}</span>
                  <span className="block font-mono text-[8px] text-zinc-500 mt-0.5">{o.hint}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* GRAB — the register pops; stuff cash until the window slams */}
      {phase === "grab" && (
        <div className="absolute inset-x-0 bottom-0 flex flex-col items-center pointer-events-none">
          {bills.map((b) => (
            <span key={b.id} className="absolute bottom-56 font-mono font-bold text-emerald-300 text-lg pointer-events-none"
              style={{ left: `${b.x}%`, transform: `rotate(${b.r}deg)`, animation: "billFly 0.9s ease-out forwards" }}>$</span>
          ))}
          <div className="pointer-events-auto w-full max-w-2xl mb-8" style={{ animation: "ncFadeUp 0.25s ease both" }}>
            {/* timer bar */}
            <div className="flex items-center gap-3 mb-2 px-1">
              <Timer className="w-4 h-4 text-amber-400" />
              <div className="flex-1 h-2 bg-black/70 border border-white/15">
                <div data-testid="rob-timer" className={`h-full transition-none ${secondsLeft < 3 ? "bg-red-500" : "bg-amber-400"}`} style={{ width: `${Math.min(100, (secondsLeft / 12) * 100)}%` }} />
              </div>
              <span data-testid="rob-seconds" className="font-mono text-sm font-bold text-amber-300 w-14 text-right">{secondsLeft.toFixed(1)}s</span>
            </div>
            {/* the register widget */}
            <button data-testid="rob-register" onClick={tapGrab} disabled={secondsLeft <= 0}
              className="w-full border-2 border-zinc-600 bg-gradient-to-b from-zinc-800 to-zinc-900 p-5 text-left active:scale-[0.99] transition-transform disabled:opacity-60"
              style={{ animation: "registerPop 0.4s ease both", boxShadow: "0 20px 60px rgba(0,0,0,0.8)" }}>
              <div className="flex items-center justify-between">
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-400">Register drawer — {secondsLeft > 0 ? "GRAB!" : "SLAMMED SHUT"}</p>
                <Banknote className="w-5 h-5 text-emerald-400" />
              </div>
              {/* bill tray */}
              <div className="grid grid-cols-6 gap-1.5 mt-3">
                {Array.from({ length: 12 }, (_, i) => (
                  <span key={i} className={`h-7 border ${i < 8 ? "bg-emerald-900/60 border-emerald-700/50" : "bg-emerald-950/30 border-emerald-900/30"} flex items-center justify-center font-mono text-[8px] text-emerald-400/70`}>$</span>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3">
                <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em]">{cashier?.line}</p>
                <p data-testid="rob-grabbed" className="font-mono font-bold text-2xl text-emerald-300">${grabbed.toLocaleString()}</p>
              </div>
            </button>
            <button data-testid="rob-escape" onClick={escape}
              className="w-full mt-2 py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.3em] bg-zinc-100 hover:bg-white text-black transition-colors flex items-center justify-center gap-2">
              <DoorOpen className="w-4 h-4" /> Get out
            </button>
          </div>
        </div>
      )}

      {/* ESCAPE — the aftermath */}
      {phase === "escape" && result && (
        <div className="absolute inset-x-0 bottom-10 flex justify-center pointer-events-none">
          <div data-testid="rob-result" className="pointer-events-auto w-full max-w-md border border-white/15 bg-black/90 backdrop-blur-md p-6 text-center" style={{ animation: "ncFadeUp 0.35s ease both" }}>
            <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-zinc-500">The aftermath</p>
            <p data-testid="rob-take" className="font-mono font-bold text-4xl mt-3" style={{ color: result.grabbed > 0 ? "#4ade80" : "#71717a" }}>
              {result.grabbed > 0 ? `+$${result.grabbed.toLocaleString()}` : "$0"}
            </p>
            <p className="font-mono text-[10px] text-zinc-400 leading-relaxed mt-3">{result.message}</p>
            {result.alarm && <p className="font-mono text-[9px] text-red-400 uppercase tracking-[0.2em] mt-2 animate-pulse">Sirens in the distance — lose them before you spend it</p>}
          </div>
        </div>
      )}
    </div>
  );
}
